/**
 * Auth context. Two modes behind VITE_USE_MOCK_AUTH:
 *   • mock  — instant local sign-in (a localStorage flag), API impersonates a
 *             seeded user by MOCK_ROLE. No tokens.
 *   • entra — Microsoft Entra External ID via MSAL redirect; access tokens are
 *             attached to API calls as Bearer.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GateResult, User } from '@cvs/shared';
import { api, setTokenProvider } from './api';
import { env } from './env';

type Status = 'loading' | 'unauthenticated' | 'authenticated';

interface AuthContextValue {
  status: Status;
  user: User | null;
  gate: GateResult | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const MOCK_FLAG = 'cvs_mock_signed_in';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [gate, setGate] = useState<GateResult | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user: u, gate: g } = await api.me();
      setUser(u);
      setGate(g);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setGate(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (env.useMockAuth) {
        setTokenProvider(async () => null);
        if (localStorage.getItem(MOCK_FLAG) === '1') await refresh();
        else if (!cancelled) setStatus('unauthenticated');
        return;
      }
      const { ensureMsalInitialized, apiScopes } = await import('./msal');
      const msal = await ensureMsalInitialized();
      const result = await msal.handleRedirectPromise();
      if (result?.account) msal.setActiveAccount(result.account);
      const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
      if (account) {
        msal.setActiveAccount(account);
        setTokenProvider(async () => {
          try {
            const r = await msal.acquireTokenSilent({ scopes: apiScopes(), account });
            return r.accessToken;
          } catch {
            return null;
          }
        });
        await refresh();
      } else if (!cancelled) {
        setStatus('unauthenticated');
      }
    }
    init().catch(() => {
      if (!cancelled) setStatus('unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async () => {
    if (env.useMockAuth) {
      localStorage.setItem(MOCK_FLAG, '1');
      setStatus('loading');
      await refresh();
      return;
    }
    const { ensureMsalInitialized, apiScopes } = await import('./msal');
    const msal = await ensureMsalInitialized();
    await msal.loginRedirect({ scopes: apiScopes() });
  }, [refresh]);

  const logout = useCallback(async () => {
    if (env.useMockAuth) {
      localStorage.removeItem(MOCK_FLAG);
      setUser(null);
      setGate(null);
      setStatus('unauthenticated');
      return;
    }
    const { ensureMsalInitialized } = await import('./msal');
    const msal = await ensureMsalInitialized();
    await msal.logoutRedirect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, gate, login, logout, refresh }),
    [status, user, gate, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
